import React, { useMemo, useRef } from "react";
import DesignerRenderer from "./renderer/DesignerRenderer";

const DesignerCanvas = ({
  page,
  objects,
  latestTags,
  selectedObjectIds = [],
  selectedGroupId = null,
  setSelectedObjectIds,
  onSelectObject,
  clearSelection,
  updateObjectPosition,
  updateObjectRect,
  updateObjectsBulk,
  updateObjectDataField,
  onButtonAction,
  onDuplicateObject,
  onDeleteObject,
  onBringForwardObject,
  onSendBackwardObject,
  onBringToFrontObject,
  onSendToBackObject,
  readOnly = false,
  hideCanvasPadding = false,
  viewportManaged = false,
}) => {
  const canvasRef = useRef(null);

  const selectedIdsSet = useMemo(
    () => new Set(selectedObjectIds),
    [selectedObjectIds]
  );

  const handleCanvasClick = () => {
    if (typeof clearSelection === "function") {
      clearSelection();
      return;
    }

    if (typeof setSelectedObjectIds === "function") {
      setSelectedObjectIds([]);
    }
  };

  const canvasStyle = {
    position: "relative",
    width: `${page.width}px`,
    height: `${page.height}px`,
    minWidth: `${page.width}px`,
    minHeight: `${page.height}px`,
    backgroundColor: page.background_color || "#ffffff",
    backgroundImage: page.show_grid
      ? `
        linear-gradient(to right, #e5e7eb 1px, transparent 1px),
        linear-gradient(to bottom, #e5e7eb 1px, transparent 1px)
      `
      : "none",
    backgroundSize: page.show_grid
      ? `${page.grid_size || 20}px ${page.grid_size || 20}px`
      : "auto",
    border: "1px solid #cbd5e1",
    overflow: "hidden",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    flex: "0 0 auto",
  };

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        maxWidth: "100%",
        maxHeight: "100%",
        overflowX: viewportManaged ? "hidden" : "auto",
        overflowY: viewportManaged ? "hidden" : "auto",
        touchAction: viewportManaged ? "none" : "auto",
        background: "#f3f4f6",
        padding: hideCanvasPadding ? 0 : 16,
        boxSizing: "border-box",
      }}
      onClick={handleCanvasClick}
    >
      <div
        style={{
          width: "max-content",
          minWidth: "100%",
          minHeight: "100%",
        }}
      >
        <div ref={canvasRef} style={canvasStyle}>
          {objects.map((obj) => {
            const isSelected =
              selectedIdsSet.has(obj.id) ||
              (selectedGroupId && obj.group === selectedGroupId);

            return (
              <DesignerRenderer
                key={obj.id}
                object={obj}
                objects={objects}
                latestTags={latestTags}
                selected={isSelected}
                selectedGroupId={selectedGroupId}
                onSelect={(event) =>
                  onSelectObject?.(
                    obj.id,
                    Boolean(event?.ctrlKey || event?.metaKey)
                  )
                }
                updateObjectPosition={updateObjectPosition}
                updateObjectRect={updateObjectRect}
                updateObjectsBulk={updateObjectsBulk}
                updateObjectDataField={updateObjectDataField}
                onButtonAction={onButtonAction}
                onDuplicateObject={onDuplicateObject}
                onDeleteObject={onDeleteObject}
                onBringForwardObject={onBringForwardObject}
                onSendBackwardObject={onSendBackwardObject}
                onBringToFrontObject={onBringToFrontObject}
                onSendToBackObject={onSendToBackObject}
                canvasRef={canvasRef}
                readOnly={readOnly}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DesignerCanvas;
